import dayjs from 'dayjs';
import type { ProColumns } from '@ant-design/pro-table';
import { IUser } from '../interfaces/user';

const TYPE_COLUMN = {
  STRING: 'STRING',
  NUMBER: 'NUMBER',
  BOOLEAN: 'BOOLEAN',
  DATE: 'DATE',
} as const;

type FormValues = Record<string, unknown>;
type ABMColumn = ProColumns & { type?: string };

const getColumnKey = (column: ABMColumn): string | undefined =>
  typeof column.dataIndex === 'string' ? column.dataIndex : undefined;

const hasOptionalRule = (column: ABMColumn): boolean => {
  const itemProps = column.formItemProps;
  const rules =
    itemProps && typeof itemProps !== 'function' ? itemProps.rules : undefined;
  return (
    !rules ||
    rules.some(
      (rule: unknown) =>
        typeof rule === 'function' ||
        (typeof rule === 'object' &&
          rule !== null &&
          (!('required' in rule) || !rule.required)),
    )
  );
};

const getFalsyValue = (column: ABMColumn): unknown => {
  if (column.type === TYPE_COLUMN.BOOLEAN) return false;
  if (column.type === TYPE_COLUMN.NUMBER) return undefined;
  return null;
};

const valuesResult = <T extends FormValues>(set: T): T => {
  return Object.fromEntries(
    Object.entries(set).filter(([, value]) => Boolean(value)),
  ) as T;
};

const valuesResultWithFalsyValues = (
  columns: ABMColumn[],
  set: FormValues,
): FormValues => {
  const values: FormValues = {};

  for (const column of columns) {
    const key = getColumnKey(column);
    if (!column.renderFormItem || !key || key === 'id') continue;

    if (set[key]) {
      values[key] = set[key];
    } else if (!column.hideInForm && hasOptionalRule(column)) {
      values[key] = getFalsyValue(column);
    }
  }

  return values;
};

const parseObjByConfigColumn = (
  columns: ABMColumn[],
  values: FormValues,
  notIgnoreFalsyValues?: boolean,
  user?: IUser,
): FormValues => {
  const parsedValues = notIgnoreFalsyValues
    ? valuesResultWithFalsyValues(columns, values)
    : valuesResult(values);
  const columnTypes = new Map<string, string>();

  for (const column of columns) {
    const key = getColumnKey(column);
    if (key && column.type) columnTypes.set(key, column.type);
  }

  for (const [key, value] of Object.entries(parsedValues)) {
    switch (columnTypes.get(key)) {
      case TYPE_COLUMN.STRING:
        parsedValues[key] = `${value}`;
        break;
      case TYPE_COLUMN.NUMBER:
        parsedValues[key] = Number(value);
        break;
      case TYPE_COLUMN.BOOLEAN:
        if (value === 'false') parsedValues[key] = false;
        if (value === 'true') parsedValues[key] = true;
        break;
      case TYPE_COLUMN.DATE: {
        const timezone =
          user?.profile?.timezone ?? import.meta.env.VITE_TIMEZONE ?? '';
        parsedValues[key] = new Date(
          dayjs(value as string | number | Date)
            .tz(timezone)
            .format(),
        ).toISOString();
        break;
      }
      default:
        break;
    }
  }

  return parsedValues;
};

const ABM = {
  TYPE_COLUMN,
  valuesResult,
  valuesResultWithFalsyValues,
  parseObjByConfigColumn,
};

export { ABM };
